import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import {
  useCreateHealthCheckRuleConfig,
  useCreateHealthCheckRuleConfigType,
  useDeleteHealthCheckRuleConfigType,
  useGetHealthCheckRuleConfigs,
  useGetHealthCheckRuleConfigTypes,
  useUpdateHealthCheckRuleConfig,
} from '../../hooks/reactQuerys/HealthCheckRuleQueries';
import { useGetLegalTransactionTypes } from '../../hooks/reactQuerys/LegalTransactionQueries';
import { IHealthCheckRuleConfig } from '../../Interfaces/IHealthCheckRule';
import { HEALTH_CHECK_CATALOG } from '../../LegalTransaction/HealthCheck/catalog';
import {
  HEALTH_CHECK_SECTIONS,
  HealthCheckSection,
  HealthCheckSeverity,
  IHealthCheckRuleDefinition,
} from '../../LegalTransaction/HealthCheck/types';
import { useAlert } from '../../context/AlertContext';
import { HealthCheckRuleRow, IRuleDraft } from './HealthCheckRuleRow';
import { HEALTH_CHECK_ROW_GRID } from './healthCheckRuleLayout';

const parseParams = (
  definition: IHealthCheckRuleDefinition,
  raw: string | null
): Record<string, number | boolean> => {
  const params: Record<string, number | boolean> = {};

  (definition.params ?? []).forEach(param => {
    params[param.key] = param.default;
  });

  if (!raw) {
    return params;
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;

    (definition.params ?? []).forEach(param => {
      const value = parsed[param.key];

      if (param.type === 'number' && typeof value === 'number') {
        params[param.key] = value;
      }

      if (param.type === 'boolean' && typeof value === 'boolean') {
        params[param.key] = value;
      }
    });
  } catch {
    // Katalog-Defaults sind das sichere Verhalten.
  }

  return params;
};

/**
 * Einstellungen → Health Check → Vorgang-Check (#1283).
 *
 * Die Liste kommt aus dem Regelkatalog im Code, der Zustand je Regel aus der
 * Datenbank. Eine Regel ohne Konfigurationszeile zeigt „Standardvorgabe" und
 * bekommt ihre Zeile erst, wenn hier etwas geändert wird. Deshalb erscheinen
 * neue Regeln nach einer Release automatisch, ohne Migration.
 */
export const HealthCheckRulesView: React.FC = () => {
  const { showAlert } = useAlert();

  const { healthCheckRuleConfigs, isLoadingHealthCheckRuleConfigs } =
    useGetHealthCheckRuleConfigs();
  const { healthCheckRuleConfigTypes, isLoadingHealthCheckRuleConfigTypes } =
    useGetHealthCheckRuleConfigTypes();
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();
  // Der Hook liefert `data` ohne Fallback und damit initial undefined.
  const transactionTypeArray = legalTransactionTypes ?? [];

  const { createHealthCheckRuleConfig } = useCreateHealthCheckRuleConfig();
  const { updateHealthCheckRuleConfig } = useUpdateHealthCheckRuleConfig();
  const { createHealthCheckRuleConfigType } =
    useCreateHealthCheckRuleConfigType();
  const { deleteHealthCheckRuleConfigType } =
    useDeleteHealthCheckRuleConfigType();

  const [isSaving, setIsSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, IRuleDraft>>({});

  const [selectedSection, setSelectedSection] = useState<HealthCheckSection>(
    HEALTH_CHECK_SECTIONS[0]
  );

  /** Regeln eines Bereichs in Katalog-Reihenfolge. */
  const rulesBySection = (target: HealthCheckSection) =>
    (
      Object.entries(HEALTH_CHECK_CATALOG) as [
        string,
        IHealthCheckRuleDefinition,
      ][]
    ).filter(([, definition]) => definition.section === target);

  /** Nur Bereiche, in denen es ueberhaupt Regeln gibt. */
  const sectionsWithRules = useMemo(
    () =>
      HEALTH_CHECK_SECTIONS.filter(target =>
        Object.values(HEALTH_CHECK_CATALOG).some(
          definition => definition.section === target
        )
      ),
    []
  );

  /** Faellt zurueck, falls der gewaehlte Bereich aus dem Katalog verschwindet. */
  const activeSection = sectionsWithRules.includes(selectedSection)
    ? selectedSection
    : sectionsWithRules[0];

  const isLoading =
    isLoadingHealthCheckRuleConfigs ||
    isLoadingHealthCheckRuleConfigTypes ||
    isLoadingLegalTransactionTypes;

  const configByRuleKey = useMemo(
    () =>
      new Map(
        healthCheckRuleConfigs.map(config => [config.RuleKey, config] as const)
      ),
    [healthCheckRuleConfigs]
  );

  const typeIdsByConfigId = useMemo(() => {
    const map = new Map<number, number[]>();

    healthCheckRuleConfigTypes.forEach(entry => {
      const existing = map.get(entry.idHealthCheckRuleConfig) ?? [];
      map.set(entry.idHealthCheckRuleConfig, [
        ...existing,
        entry.idLegalTransactionType,
      ]);
    });

    return map;
  }, [healthCheckRuleConfigTypes]);

  /** Gespeicherter Zustand einer Regel; Basis für den Entwurf. */
  const persistedDraft = (
    ruleKey: string,
    definition: IHealthCheckRuleDefinition
  ): IRuleDraft => {
    const config = configByRuleKey.get(ruleKey);

    if (config === undefined) {
      return {
        isActive: definition.defaultActive,
        severity: definition.defaultSeverity,
        scopeMode: 'all',
        idLegalTransactionTypeArray: [],
        params: parseParams(definition, null),
      };
    }

    return {
      isActive: config.IsActive,
      severity:
        (config.Severity as HealthCheckSeverity | null) ??
        definition.defaultSeverity,
      scopeMode: config.ScopeMode,
      idLegalTransactionTypeArray:
        typeIdsByConfigId.get(config.idHealthCheckRuleConfig) ?? [],
      params: parseParams(definition, config.Params),
    };
  };

  const draftOf = (ruleKey: string, definition: IHealthCheckRuleDefinition) =>
    drafts[ruleKey] ?? persistedDraft(ruleKey, definition);

  const patchDraft = (
    ruleKey: string,
    definition: IHealthCheckRuleDefinition,
    patch: Partial<IRuleDraft>
  ) => {
    setDrafts(current => ({
      ...current,
      [ruleKey]: { ...draftOf(ruleKey, definition), ...patch },
    }));
  };

  const isDirty = (ruleKey: string, definition: IHealthCheckRuleDefinition) => {
    const draft = drafts[ruleKey];

    if (draft === undefined) {
      return false;
    }

    const persisted = persistedDraft(ruleKey, definition);

    return (
      draft.isActive !== persisted.isActive ||
      draft.severity !== persisted.severity ||
      draft.scopeMode !== persisted.scopeMode ||
      JSON.stringify([...draft.idLegalTransactionTypeArray].sort()) !==
        JSON.stringify([...persisted.idLegalTransactionTypeArray].sort()) ||
      JSON.stringify(draft.params) !== JSON.stringify(persisted.params)
    );
  };

  /** Regeln mit ungespeicherter Aenderung — ueber alle Bereiche hinweg. */
  const dirtyRuleKeys = Object.keys(drafts).filter(ruleKey =>
    isDirty(ruleKey, HEALTH_CHECK_CATALOG[ruleKey])
  );

  /**
   * Greift ueberhaupt eine Pruefung? Ausgeliefert ist alles aus, und ohne
   * diesen Hinweis sieht ein Administrator 28 Schalter, ohne zu erfahren, dass
   * der Health Check damit nichts tut.
   */
  const hasActiveRule = (
    Object.entries(HEALTH_CHECK_CATALOG) as [
      string,
      IHealthCheckRuleDefinition,
    ][]
  ).some(([ruleKey, definition]) => draftOf(ruleKey, definition).isActive);

  /** Verknüpfte Rechtsgeschäftsarten auf den Entwurf bringen. */
  const syncTypeLinks = async (
    idHealthCheckRuleConfig: number,
    nextTypeIds: number[]
  ) => {
    const existing = healthCheckRuleConfigTypes.filter(
      entry => entry.idHealthCheckRuleConfig === idHealthCheckRuleConfig
    );

    const toRemove = existing.filter(
      entry => !nextTypeIds.includes(entry.idLegalTransactionType)
    );
    const existingTypeIds = existing.map(entry => entry.idLegalTransactionType);
    const toAdd = nextTypeIds.filter(id => !existingTypeIds.includes(id));

    for (const entry of toRemove) {
      await deleteHealthCheckRuleConfigType(entry.idHealthCheckRuleConfigType);
    }

    for (const idLegalTransactionType of toAdd) {
      await createHealthCheckRuleConfigType({
        idHealthCheckRuleConfig,
        idLegalTransactionType,
      });
    }
  };

  /** Schreibt eine Regel. Wirft weiter, damit der Aufrufer zaehlen kann. */
  const persistRule = async (
    ruleKey: string,
    definition: IHealthCheckRuleDefinition
  ) => {
    const draft = draftOf(ruleKey, definition);

    const payload: Partial<IHealthCheckRuleConfig> = {
      RuleKey: ruleKey,
      IsActive: draft.isActive,
      Severity: draft.severity,
      ScopeMode: draft.scopeMode,
      Params:
        (definition.params ?? []).length > 0
          ? JSON.stringify(draft.params)
          : null,
    };

    const existing = configByRuleKey.get(ruleKey);
    let idHealthCheckRuleConfig = existing?.idHealthCheckRuleConfig;

    if (existing === undefined) {
      const created = (await createHealthCheckRuleConfig(
        payload
      )) as IHealthCheckRuleConfig;
      idHealthCheckRuleConfig = created?.idHealthCheckRuleConfig;
    } else {
      await updateHealthCheckRuleConfig({
        ...existing,
        ...payload,
      } as IHealthCheckRuleConfig);
    }

    if (idHealthCheckRuleConfig !== undefined) {
      await syncTypeLinks(
        idHealthCheckRuleConfig,
        draft.scopeMode === 'all' ? [] : draft.idLegalTransactionTypeArray
      );
    }
  };

  /**
   * Speichert alle geaenderten Regeln in einem Zug.
   *
   * Vorher hatte jede Regel ihren eigenen Knopf — wer den Health Check
   * einrichtet, schaltet aber ein Dutzend Pruefungen frei und musste ebenso oft
   * speichern. Reihenfolge statt parallel, damit die Schreibvorgaenge sich
   * nicht ins Gehege kommen; eine gescheiterte Regel stoppt die uebrigen nicht,
   * ihr Entwurf bleibt aber stehen, damit nichts unbemerkt verlorengeht.
   */
  const handleSaveAll = async () => {
    setIsSaving(true);
    const failed: string[] = [];
    const saved: string[] = [];

    try {
      for (const ruleKey of dirtyRuleKeys) {
        const definition = HEALTH_CHECK_CATALOG[ruleKey];

        try {
          await persistRule(ruleKey, definition);
          saved.push(ruleKey);
        } catch {
          failed.push(definition.title);
        }
      }

      setDrafts(current => {
        const next = { ...current };
        saved.forEach(ruleKey => delete next[ruleKey]);
        return next;
      });

      if (failed.length > 0) {
        showAlert({
          text:
            failed.length === 1
              ? `„${failed[0]}" konnte nicht gespeichert werden.`
              : `${failed.length} Prüfungen konnten nicht gespeichert werden: ${failed.join(', ')}.`,
          severity: 'error',
        });
      } else {
        showAlert({
          text:
            saved.length === 1
              ? 'Die Änderung wurde gespeichert.'
              : `${saved.length} Änderungen wurden gespeichert.`,
          severity: 'success',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Verwirft alle noch nicht gespeicherten Aenderungen.
   *
   * Bewusst rein lokal: es gibt kein „Zuruecksetzen auf die Vorgabe", das die
   * Konfigurationszeilen loeschen wuerde. Der Katalog-Default ist nur ein
   * Startwert — hat eine Kanzlei sich entschieden, ist die Rueckkehr zu unserer
   * Vorgabe kein sinnvolles Ziel.
   */
  const handleDiscardAll = () => setDrafts({});

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant='h6'>Vorgang-Check</Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Welche Prüfungen beim Speichern eines Vorgangs laufen. Die Prüfungen
        verhindern das Speichern nie — sie werden gesammelt angezeigt. Hinweise
        zählen nicht in die Warnungsanzahl.
      </Typography>

      {/*
        Ausgeliefert ist jede Pruefung aus. Ohne diesen Hinweis sieht ein
        Administrator nur eine Liste ausgeschalteter Schalter und erfaehrt
        nirgends, dass der Health Check damit gar nichts meldet.
      */}
      {!hasActiveRule && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Derzeit ist keine Prüfung aktiv — der Health Check meldet in keinem
          Vorgang etwas. Schalten Sie unten die Prüfungen ein, die Ihre Kanzlei
          haben möchte, und speichern Sie oben rechts.
        </Alert>
      )}

      <Alert severity='info' sx={{ mb: 2 }}>
        In einem Vorgang können einzelne Prüfungen abweichend ein- oder
        ausgeschaltet werden. Diese Abweichung gilt nur für den jeweiligen
        Vorgang und steht in dessen Log.
      </Alert>

      {/*
        Ein Speichern-Knopf fuer alles. Vorher hatte jede Regel einen eigenen —
        wer den Health Check einrichtet, schaltet aber ein Dutzend Pruefungen
        frei und musste ebenso oft speichern. Die Leiste klebt oben, damit sie
        beim Scrollen durch 28 Regeln erreichbar bleibt.
      */}
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          py: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant='body2' color='text.secondary'>
          {dirtyRuleKeys.length === 0
            ? 'Keine ungespeicherten Änderungen'
            : dirtyRuleKeys.length === 1
              ? '1 Prüfung geändert'
              : `${dirtyRuleKeys.length} Prüfungen geändert`}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {dirtyRuleKeys.length > 0 && (
          <Button size='small' disabled={isSaving} onClick={handleDiscardAll}>
            Verwerfen
          </Button>
        )}
        <Button
          variant='contained'
          size='small'
          disabled={dirtyRuleKeys.length === 0 || isSaving}
          onClick={() => void handleSaveAll()}
        >
          {isSaving ? 'Speichert…' : 'Speichern'}
        </Button>
      </Stack>

      {/*
        Unterreiter je Bereich statt aller Bereiche untereinander: mit rund 30
        Regeln wird die Seite sonst extrem lang.

        Bewusst ohne Zahl am Reiter. Sie zaehlte die Regeln mit eigener
        Konfigurationszeile, also alles einmal Angefasste — und wurde als „so
        viele sind hier deaktiviert" gelesen. Eine Zahl, die etwas anderes
        bedeutet als das, was der Leser darin sieht, ist schlechter als keine.
      */}
      <Tabs
        value={activeSection}
        onChange={(_event, value: HealthCheckSection) =>
          setSelectedSection(value)
        }
        variant='scrollable'
        scrollButtons='auto'
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {sectionsWithRules.map(section => (
          <Tab key={section} value={section} label={section} />
        ))}
      </Tabs>

      {/*
        Kopfzeile der Liste. Teilt sich die Grid-Definition mit den Zeilen und
        verschwindet unterhalb von `lg` mit ihnen — dort stapeln sich die
        Bedienelemente unter dem Titel, eine Spaltenueberschrift waere dann
        gegenstandslos.
      */}
      <Box
        sx={{
          ...HEALTH_CHECK_ROW_GRID,
          display: { xs: 'none', lg: 'grid' },
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {(
          [
            ['active', 'Aktiv'],
            ['rule', 'Prüfung'],
            ['level', 'Stufe'],
            ['scope', 'Gilt für'],
            ['types', 'Rechtsgeschäftsarten'],
            ['action', 'Aktion'],
          ] as const
        ).map(([area, label]) => (
          <Typography
            key={area}
            variant='caption'
            color='text.secondary'
            sx={{ gridArea: area, fontWeight: 600 }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Stack divider={<Divider />} spacing={0} sx={{ mb: 3 }}>
        {rulesBySection(activeSection).map(([ruleKey, definition]) => (
          <HealthCheckRuleRow
            key={ruleKey}
            definition={definition}
            draft={draftOf(ruleKey, definition)}
            dirty={isDirty(ruleKey, definition)}
            transactionTypeArray={transactionTypeArray}
            onPatch={patch => patchDraft(ruleKey, definition, patch)}
          />
        ))}
      </Stack>
    </Box>
  );
};
